import { List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { styled } from '@mui/material/styles';

import React, { CSSProperties } from 'react';
import { ReactComponent as BluViuMobile } from '../assets/blu-viu-sidebar-closed.svg';
import { ReactComponent as BluViuDesktop } from '../assets/blu-viu-sidebar-open.svg';

import { useIntl } from 'react-intl';
import { NavLink } from 'react-router-dom';

import { NavigationGroup, NavigationItem, hasAccess } from 'components/layout/utils/navigation';
import { useAccountState } from 'contexts/account/account-context';
import { LucideIcon } from 'lucide-react';
import capitalizeWords from 'utils/capitalize-words';
import googleTagManager from 'utils/google-tag-manager';
import Tooltip from '../../tooltip';

type ListItemProps = {
  sidebarOpen: boolean;
  navigation: NavigationGroup[] | undefined;
};

const SIDEBAR_PREFIX = 'SidebarExpanded';
const sidebarClasses = {
  listItem: `${SIDEBAR_PREFIX}-listItem`,
  listItemActive: `${SIDEBAR_PREFIX}-listItemActive`,
  icon: `${SIDEBAR_PREFIX}-icon`,
};

const Logo = styled('div')(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  display: 'flex',
  verticalAlign: 'middle',
  minHeight: 114, // fixes height of logo to align with tabs header for each page
  padding: theme.spacing(10, 0, 1),
}));

const ListWrapper = styled('div', { label: 'ListWrapper' })<{ open: boolean }>(({ theme, open }) => ({
  width: open ? 220 : `calc(${theme.spacing(10)})`,
  margin: '0 auto',
}));

const SideBarList = styled(List, { label: 'SideBarList' })<{ open: boolean }>(({ theme, open }) => ({
  width: open ? 220 : `calc(${theme.spacing(10)})`,
  borderBottom: '1px solid white',

  color: theme.palette.common.white,
  '&:hover': {
    color: theme.palette.common.white,
  },

  [`& .${sidebarClasses.icon}`]: {
    lineHeight: 1,
    width: 18,
    color: theme.palette.common.white,
  },

  [`& .${sidebarClasses.listItemActive}`]: {
    color: theme.palette.beige.main,
    fill: theme.palette.beige.main,
    '&:hover': {
      color: theme.palette.beige.main,
    },
  },

  [`& .${sidebarClasses.listItemActive} .${sidebarClasses.icon}`]: {
    color: theme.palette.darkBlue.main,
    borderRadius: 30,
    width: 36,
    height: 36,
    background: theme.palette.beige.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  [`& .${sidebarClasses.listItemActive} .${sidebarClasses.icon} svg.deliveryBoardIcon`]: {
    color: theme.palette.beige.main,
    stroke: `${theme.palette.darkBlue.main} !important`,
  },
}));

const SidebarItems: React.FC<ListItemProps> = ({ sidebarOpen, navigation }: ListItemProps) => {
  const intl = useIntl();
  const { account } = useAccountState();
  const isOverview = location.pathname.split('/')[1] === 'overview';
  const isDeliveryBoard = location.pathname.split('/')[1] === 'delivery-board';

  const getIcon = (IconComp: LucideIcon, sx?: CSSProperties) => {
    return <IconComp size={18} style={sx} />;
  };

  const getItemLabel = (title: string, useCustomerName?: boolean) => {
    if (useCustomerName && account.company?.customerName) {
      return capitalizeWords(account.company.customerName);
    }
    return intl.formatMessage({ id: title });
  };

  const handleItemClick = (event: React.MouseEvent, item: NavigationItem) => {
    event.stopPropagation();
    googleTagManager.push({
      event: 'navigationClick',
      clickText: getItemLabel(item.title, item.useCustomerName),
      pageUrl: item.url ?? window.location.pathname,
    });
    item.onClick?.();
  };

  const getItemContent = (item: NavigationItem) => (
    <>
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: sidebarOpen ? 3 : 'auto',
          justifyContent: 'center',
        }}
        className={sidebarClasses.icon}>
        {getIcon(item.icon || item.customIcon)}
      </ListItemIcon>
      <ListItemText
        primary={getItemLabel(item.title, item.useCustomerName)}
        slotProps={{ primary: { noWrap: true } }}
        sx={{ opacity: sidebarOpen ? 1 : 0, minWidth: 0 }}
      />
    </>
  );

  return (
    <>
      <Logo>{sidebarOpen ? <BluViuDesktop /> : <BluViuMobile />}</Logo>
      <ListWrapper open={sidebarOpen}>
        {navigation?.map((group) => (
          <SideBarList open={sidebarOpen} key={group.name}>
            {group.items.map((item, index) =>
              hasAccess(
                item,
                account.company!.features!,
                account.company!.userCompanyFeatures,
                account.currentUser?.userFeatures ?? [],
                account.currentUser?.userType,
                account.company?.marketId,
                account.currentUser?.id
              ) ? (
                <Tooltip
                  key={item.title}
                  title={
                    item.useCustomerName || ((!isOverview || !isDeliveryBoard) && !sidebarOpen)
                      ? getItemLabel(item.title, item.useCustomerName)
                      : ''
                  }
                  arrow
                  placement="left">
                  <ListItem key={`${item.title}_${index}`} disablePadding sx={{ display: 'block' }}>
                    {item.url ? (
                      <ListItemButton
                        component={NavLink}
                        to={item.url}
                        onClick={(event) => handleItemClick(event, item)}
                        className={sidebarClasses.listItem}
                        activeClassName={sidebarClasses.listItemActive}
                        href={item.url}
                        sx={{
                          justifyContent: sidebarOpen ? 'initial' : 'center',
                          px: 2.5,
                        }}>
                        {getItemContent(item)}
                      </ListItemButton>
                    ) : (
                      <ListItemButton
                        onClick={(event) => handleItemClick(event, item)}
                        className={sidebarClasses.listItem}
                        sx={{
                          justifyContent: sidebarOpen ? 'initial' : 'center',
                          px: 2.5,
                        }}>
                        {getItemContent(item)}
                      </ListItemButton>
                    )}
                  </ListItem>
                </Tooltip>
              ) : null
            )}
          </SideBarList>
        ))}
      </ListWrapper>
    </>
  );
};

export default SidebarItems;
